import { useState } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Plus, Users } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { LogoUpload } from './logo-upload';

interface CompanySetupWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete?: () => void;
}

export function CompanySetupWizard({ open, onOpenChange, onComplete }: CompanySetupWizardProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [company, setCompany] = useState({
    // Basic Information
    name: '',
    description: '',
    website: '',
    email: '',
    phone: '',
    headquarters_location: '',
    industry: '',
    size_range: '',
    founded_year: new Date().getFullYear(),
    company_type: 'startup',
    logo_url: '',
    id: '',
    
    // Registration & Tax
    registration_number: '',
    tax_id: '',
    
    // Social & Online
    social_links: {
      linkedin: '',
      twitter: '',
      facebook: '',
      instagram: ''
    },
    
    // Business Details
    client_tier: 'startup',
    status: 'prospect',
    annual_contract_value: 0,
    customer_ltv: 0,
    contract_renewal_date: '',
    satisfaction_score: 100,
    
    // Settings & Preferences
    settings: {
      hiring_preferences: {
        remote_work: true,
        interview_rounds: 3,
        assessment_required: true
      },
      notification_preferences: {
        email_updates: true,
        slack_notifications: false
      },
      compliance_settings: {
        gdpr_compliant: true,
        ccpa_compliant: true
      }
    }
  });

  const handleCreate = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Create company record
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .insert([{
          name: company.name,
          description: company.description,
          website: company.website,
          email: company.email,
          phone: company.phone,
          headquarters_location: company.headquarters_location,
          industry: company.industry,
          size_range: company.size_range,
          founded_year: company.founded_year,
          company_type: company.company_type,
          registration_number: company.registration_number,
          tax_id: company.tax_id,
          social_links: company.social_links,
          client_tier: company.client_tier,
          status: company.status,
          annual_contract_value: company.annual_contract_value,
          customer_ltv: company.customer_ltv,
          contract_renewal_date: company.contract_renewal_date,
          satisfaction_score: company.satisfaction_score,
          settings: company.settings,
          admin_id: user.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }])
        .select()
        .single();

      if (companyError) throw companyError;

      // Update user's profile with company association
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          primary_company_id: companyData.id,
          current_mode: 'hire',
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (profileError) throw profileError;

      toast({
        title: 'Success',
        description: 'Company profile created successfully',
      });

      onComplete?.();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error creating company:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to create company profile',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Company Setup</DialogTitle>
          <DialogDescription>
            Set up your company profile to get started with hiring
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="business">Business</TabsTrigger>
            <TabsTrigger value="legal">Legal & Tax</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-4 mt-4">
            <div className="space-y-4">
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <Avatar className="h-24 w-24 border-2 border-muted bg-white">
                    <AvatarImage 
                      src={company.logo_url} 
                      alt={company.name} 
                      className="object-contain p-1"
                      style={{ aspectRatio: 'auto' }}
                    />
                    <AvatarFallback>
                      {company.name?.[0]?.toUpperCase() || 'C'}
                    </AvatarFallback>
                  </Avatar>
                  {company.id && (
                    <LogoUpload
                      companyId={company.id}
                      onUploadComplete={(url) => setCompany({ ...company, logo_url: url })}
                    />
                  )}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Company Name</label>
                <Input
                  value={company.name}
                  onChange={(e) => setCompany({ ...company, name: e.target.value })}
                  placeholder="Enter company name"
                />
              </div>

              <div>
                <label className="text-sm font-medium">Description</label>
                <Textarea
                  value={company.description}
                  onChange={(e) => setCompany({ ...company, description: e.target.value })}
                  placeholder="Brief company description"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Industry</label>
                  <Select
                    value={company.industry}
                    onValueChange={(value) => setCompany({ ...company, industry: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="technology">Technology</SelectItem>
                      <SelectItem value="healthcare">Healthcare</SelectItem>
                      <SelectItem value="finance">Finance</SelectItem>
                      <SelectItem value="education">Education</SelectItem>
                      <SelectItem value="retail">Retail</SelectItem>
                      <SelectItem value="manufacturing">Manufacturing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium">Company Size</label>
                  <Select
                    value={company.size_range}
                    onValueChange={(value) => setCompany({ ...company, size_range: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select size range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-10">1-10 employees</SelectItem>
                      <SelectItem value="11-50">11-50 employees</SelectItem>
                      <SelectItem value="51-200">51-200 employees</SelectItem>
                      <SelectItem value="201-500">201-500 employees</SelectItem>
                      <SelectItem value="501-1000">501-1000 employees</SelectItem>
                      <SelectItem value="1001+">1001+ employees</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Founded Year</label>
                  <Input
                    type="number"
                    value={company.founded_year}
                    onChange={(e) => setCompany({ ...company, founded_year: parseInt(e.target.value) })}
                    min={1900}
                    max={new Date().getFullYear()}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Company Type</label>
                  <Select
                    value={company.company_type}
                    onValueChange={(value) => setCompany({ ...company, company_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select company type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="startup">Startup</SelectItem>
                      <SelectItem value="smb">Small/Medium Business</SelectItem>
                      <SelectItem value="enterprise">Enterprise</SelectItem>
                      <SelectItem value="nonprofit">Non-Profit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="business" className="space-y-4 mt-4">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Email</label>
                  <Input
                    type="email"
                    value={company.email}
                    onChange={(e) => setCompany({ ...company, email: e.target.value })}
                    placeholder="contact@company.com"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Phone</label>
                  <Input
                    value={company.phone}
                    onChange={(e) => setCompany({ ...company, phone: e.target.value })}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Website</label>
                <Input
                  type="url"
                  value={company.website}
                  onChange={(e) => setCompany({ ...company, website: e.target.value })}
                  placeholder="https://company.com"
                />
              </div>

              <div>
                <label className="text-sm font-medium">Headquarters Location</label>
                <Input
                  value={company.headquarters_location}
                  onChange={(e) => setCompany({ ...company, headquarters_location: e.target.value })}
                  placeholder="City, Country"
                />
              </div>

              <div>
                <label className="text-sm font-medium">Social Links</label>
                <div className="space-y-2">
                  <Input
                    value={company.social_links.linkedin}
                    onChange={(e) => setCompany({
                      ...company,
                      social_links: { ...company.social_links, linkedin: e.target.value }
                    })}
                    placeholder="LinkedIn URL"
                  />
                  <Input
                    value={company.social_links.twitter}
                    onChange={(e) => setCompany({
                      ...company,
                      social_links: { ...company.social_links, twitter: e.target.value }
                    })}
                    placeholder="Twitter URL"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Client Tier</label>
                  <Select
                    value={company.client_tier}
                    onValueChange={(value) => setCompany({ ...company, client_tier: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select tier" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="enterprise">Enterprise</SelectItem>
                      <SelectItem value="growth">Growth</SelectItem>
                      <SelectItem value="startup">Startup</SelectItem>
                      <SelectItem value="prospect">Prospect</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium">Status</label>
                  <Select
                    value={company.status}
                    onValueChange={(value) => setCompany({ ...company, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="client">Active Client</SelectItem>
                      <SelectItem value="prospect">Prospect</SelectItem>
                      <SelectItem value="past_client">Past Client</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="legal" className="space-y-4 mt-4">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Registration Number</label>
                <Input
                  value={company.registration_number}
                  onChange={(e) => setCompany({ ...company, registration_number: e.target.value })}
                  placeholder="Company registration number"
                />
              </div>

              <div>
                <label className="text-sm font-medium">Tax ID</label>
                <Input
                  value={company.tax_id}
                  onChange={(e) => setCompany({ ...company, tax_id: e.target.value })}
                  placeholder="Tax identification number"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Annual Contract Value</label>
                  <Input
                    type="number"
                    value={company.annual_contract_value}
                    onChange={(e) => setCompany({ ...company, annual_contract_value: parseFloat(e.target.value) })}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Customer LTV</label>
                  <Input
                    type="number"
                    value={company.customer_ltv}
                    onChange={(e) => setCompany({ ...company, customer_ltv: parseFloat(e.target.value) })}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Contract Renewal Date</label>
                <Input
                  type="date"
                  value={company.contract_renewal_date}
                  onChange={(e) => setCompany({ ...company, contract_renewal_date: e.target.value })}
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="settings" className="space-y-4 mt-4">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Hiring Preferences</label>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Remote Work</span>
                    <Select
                      value={company.settings.hiring_preferences.remote_work.toString()}
                      onValueChange={(value) => setCompany({
                        ...company,
                        settings: {
                          ...company.settings,
                          hiring_preferences: {
                            ...company.settings.hiring_preferences,
                            remote_work: value === 'true'
                          }
                        }
                      })}
                    >
                      <SelectTrigger className="w-[120px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">Yes</SelectItem>
                        <SelectItem value="false">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm">Interview Rounds</span>
                    <Input
                      type="number"
                      className="w-[120px]"
                      value={company.settings.hiring_preferences.interview_rounds}
                      onChange={(e) => setCompany({
                        ...company,
                        settings: {
                          ...company.settings,
                          hiring_preferences: {
                            ...company.settings.hiring_preferences,
                            interview_rounds: parseInt(e.target.value)
                          }
                        }
                      })}
                      min={1}
                      max={10}
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Notification Preferences</label>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Email Updates</span>
                    <Select
                      value={company.settings.notification_preferences.email_updates.toString()}
                      onValueChange={(value) => setCompany({
                        ...company,
                        settings: {
                          ...company.settings,
                          notification_preferences: {
                            ...company.settings.notification_preferences,
                            email_updates: value === 'true'
                          }
                        }
                      })}
                    >
                      <SelectTrigger className="w-[120px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">Enabled</SelectItem>
                        <SelectItem value="false">Disabled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Compliance Settings</label>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">GDPR Compliant</span>
                    <Select
                      value={company.settings.compliance_settings.gdpr_compliant.toString()}
                      onValueChange={(value) => setCompany({
                        ...company,
                        settings: {
                          ...company.settings,
                          compliance_settings: {
                            ...company.settings.compliance_settings,
                            gdpr_compliant: value === 'true'
                          }
                        }
                      })}
                    >
                      <SelectTrigger className="w-[120px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">Yes</SelectItem>
                        <SelectItem value="false">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={loading}>
            {loading ? 'Creating...' : 'Create Company'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}